import ArrowIcon from '~/assets/svg/newSvg/arrow-select.svg?react';
import CloseIcon from '~/assets/svg/newSvg/close-purple.svg?react';
import { useState, useEffect } from 'react';
import cn from 'classnames';
import Option from './Option/Option';
import { SearchInput } from './SearchInput/SearchInput';

import styles from './CustomQuerySelect.module.scss';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'antd';

export interface IOption {
  id: number;
  name: string;
  full_name?: string;
  code?: string;
}

interface IMultiSelectProps {
  placeholder: string;
  value: IOption[];
  disabled: boolean | undefined;
  onDeleteClick: (option: IOption) => void;
}

const MultiSelect = ({
  value,
  placeholder,
  disabled,
  onDeleteClick,
}: IMultiSelectProps) => {
  if (value?.length) {
    return (
      <ul className={styles.multiSelect}>
        {value.map(value => {
          return (
            <li key={value.id} className={styles.selectedOption}>
              {value.full_name || value.name}
              <button
                onClick={
                  disabled
                    ? undefined
                    : event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onDeleteClick(value);
                      }
                }
                type="button"
                className={styles.selectedOptionDelete}
              >
                <CloseIcon />
              </button>
            </li>
          );
        })}
      </ul>
    );
  }
  return <span className={styles.placeholder}>{placeholder}</span>;
};

interface ISelectProps {
  placeholder: string;
  value: IOption | IOption[] | undefined;
}

const SingleSelect = ({ placeholder, value }: ISelectProps) => {
  if (!Array.isArray(value) && value?.name) {
    return (
      <span className={styles.value}>
        {value.full_name
          ? value.full_name && value.code
            ? value.full_name + ' ' + value?.code
            : value.full_name
          : value?.name}
      </span>
    );
  }
  return <span className={styles.placeholder}>{placeholder}</span>;
};

interface ICustomQuerySelectProps {
  label?: string;
  placeholder?: string;
  value?: IOption | IOption[] | undefined;
  disabled?: boolean;
  multiselect?: boolean;
  isSearchEnabled?: boolean;
  required?: boolean;
  error?: string;
  query: string;
  queryKey: [string, any?];
  params: any;
  onChange: (option: IOption | IOption[]) => void;
  onTouch?: () => void;
  className?: string;
  hasCheckbox?: boolean;
  setIsCheckedAll?: (value: boolean) => void;
  checkboxLabel?: string;
}

const CustomQuerySelect = ({
  label,
  placeholder = 'Select option',
  value,
  disabled,
  multiselect,
  isSearchEnabled,
  required,
  error,
  params,
  query,
  queryKey,
  onTouch,
  onChange,
  className,
  hasCheckbox,
  setIsCheckedAll,
  checkboxLabel,
}: ICustomQuerySelectProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isChecked, setIsChecked] = useState(false);
  const [prevSelectedItems, setPrevSelectedItems] = useState(value);
  const debouncedSearchTerm = useDebounce(searchValue, 500);
  const { t } = useTranslation();

  useEffect(() => {
    if (Array.isArray(value) && value.length === 0) {
      setIsChecked(false);
    }
  }, [value]);

  const handleToggleSelect = () => {
    setIsOpen(!isOpen);
  };

  const { data } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(query, {
        params: { ...params, query: debouncedSearchTerm },
      });
      return data.data || data;
    },
    queryKey: [...queryKey, debouncedSearchTerm],
  });

  const handleSelectOption = (option: IOption) => {
    if (multiselect && Array.isArray(value)) {
      const updatedValues = value.find((item: IOption) => item.id === option.id)
        ? value.filter((item: IOption) => {
            return item.id !== option.id;
          })
        : [...value, option];
      setPrevSelectedItems(updatedValues);
      onChange(updatedValues);
    } else {
      onChange(option);
      setIsOpen(false);
    }
  };

  const handleCloseDropdown = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleCheck = (checked: boolean) => {
    setIsChecked(checked);
    setPrevSelectedItems(value);
    if (setIsCheckedAll) {
      setIsCheckedAll(checked);
    }
    if (checked) {
      onChange(data);
    } else {
      onChange(prevSelectedItems || []);
    }
  };

  const handleDiscard = () => {
    setIsChecked(false);
    if (setIsCheckedAll) {
      setIsCheckedAll(false);
    }
    onChange([]);
  };

  return (
    <div
      className={cn(
        styles.customSelect,
        className,
        multiselect ? styles.customSelectMultiselect : undefined,
        isOpen ? styles.customSelectOpen : undefined,
        disabled ? styles.customSelectDisabled : undefined
      )}
      onBlur={event => {
        handleCloseDropdown(event);
        if (onTouch) {
          onTouch();
        }
      }}
    >
      {label ? (
        <span className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </span>
      ) : (
        ''
      )}
      <button
        type="button"
        onClick={disabled ? undefined : handleToggleSelect}
        className={cn(
          styles.customSelectLabel,
          isOpen ? styles.customSelectLabelOpen : undefined
        )}
      >
        {multiselect && Array.isArray(value) ? (
          <MultiSelect
            value={value}
            disabled={disabled}
            onDeleteClick={handleSelectOption}
            placeholder={placeholder}
          />
        ) : (
          <SingleSelect value={value} placeholder={placeholder} />
        )}
        <ArrowIcon
          className={cn(
            styles.arrowIcon,
            isOpen ? styles.arrowIconOpen : undefined
          )}
        />
      </button>
      {isOpen ? (
        <ul className={styles.options}>
          {isSearchEnabled && (
            <div className={styles.searchWrapper}>
              <SearchInput
                value={searchValue}
                onChange={e => {
                  setSearchValue(e.target.value);
                }}
                onClearPress={() => {
                  setSearchValue('');
                }}
              />
            </div>
          )}

          {data?.length === 0 ? (
            <span className={styles.noResultText}>{t('no_results')}</span>
          ) : (
            <>
              {data?.map(
                (option: {
                  id: number;
                  name: string;
                  full_name?: string;
                  code?: string;
                }) => (
                  <Option
                    key={option.id}
                    onClick={() => handleSelectOption(option)}
                    code={option.code}
                    name={option.full_name || option.name}
                    isSelected={
                      Array.isArray(value)
                        ? !!value.find((item: IOption) => item.id === option.id)
                        : value?.id === option.id
                    }
                  />
                )
              )}
            </>
          )}
        </ul>
      ) : (
        ''
      )}
      {error && <span className={styles.error}>{error}</span>}
      {hasCheckbox && multiselect && (
        <div className={styles.checkboxWrapper}>
          <label>
            <Checkbox
              checked={isChecked}
              onChange={e => handleCheck(e.target.checked)}
            />
            <span className={styles.checkboxLabel}>
              {checkboxLabel || t('all_sections')}
            </span>
          </label>
          {!value ||
            (Array.isArray(value) && !!value.length && (
              <button
                type="button"
                onClick={handleDiscard}
                className={styles.discardBtn}
              >
                {t('discard')}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default CustomQuerySelect;
